import { HttpError, httpErrorBodyMessage } from './http-error';
import { ApiResponse } from '../types/api';
import { HD_SOURCE_HEADER, HD_SOURCE_UX } from './hd-source';
import { aiRouterUrlForApiHost, setAiRouterBaseUrl } from '../hubby-core/ai-router-fetch';

const defaultHeaders = {
    'Content-Type': 'application/json',
    'hd-version': '3.0',
    Accept: 'application/json'
};

export let apiHost =
    localStorage.getItem('apihost-override') ||
    (window.location.hostname === 'localhost' ? 'hd-test01.com' : window.location.hostname);

export function setApiHost(host: string) {
    apiHost = host;
}

// Hubby's ai-router follows the API host: the test worker only accepts the test API's host, so
// test01, the dev boxes and an apihost-override pointed at either one all land on the matching
// deployment. Read per call, so a later setApiHost (host info, the debug panel) takes effect.
setAiRouterBaseUrl(() => aiRouterUrlForApiHost(apiHost));

export function apiUrlFetch(path: string) {
    return `${apiHost ? 'https://' + apiHost : ''}${apiUrl(path)}`;
}

export function apiUrl(path: string) {
    return path.startsWith('/') ? path : `/${path}`;
}

function jsonBodyAsRecord(value: unknown): Record<string, unknown> {
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        return value as Record<string, unknown>;
    }
    return {};
}

/**
 * Thrown for non-2xx responses. Carries the status so callers can tell a rejected
 * request (401/403) from a server-side or transient one; a request that never reached
 * the server rejects with the underlying fetch TypeError instead and has no status.
 */
// Defined in http-error.ts (no browser access while loading) and re-exported here so every
// existing import is unaffected.
export { HttpError };

export async function request<T, U = unknown>(
    method: RequestInit['method'],
    path: string,
    data?: U,
    opts?: RequestInit
): Promise<ApiResponse<T>> {
    const url = path.includes('https://') ? path : apiUrlFetch(path);

    /*
     * ROBO-750. Names this caller to the API's audit log. A caller's own value wins (the Hubby panel
     * sends its own for the calls its tools make). API requests only: on any other host it would be
     * one more custom header for that host's CORS preflight to refuse.
     */
    const toApi = !path.includes('https://') || path.startsWith(`https://${apiHost}`);

    const headers: HeadersInit = {
        ...defaultHeaders,
        ...(toApi ? { [HD_SOURCE_HEADER]: HD_SOURCE_UX } : {}),
        ...opts?.headers
    };

    const options = {
        method,
        headers,
        keepalive: opts?.keepalive,
        // The browser's HTTP cache can otherwise serve a stale GET (e.g. host.hd.pics or
        // brands) straight from disk on reload, silently undoing an already-successful save.
        cache: 'no-store' as RequestCache,
        ...(method !== 'GET' && data
            ? { body: data instanceof FormData ? data : JSON.stringify(data) }
            : {})
    };

    const response = await fetch(url, options);

    if (!response.ok) {
        const raw = await response.json().catch(() => ({}));
        const errorData = jsonBodyAsRecord(raw);
        /*
         * ROBO-593. The body's own words, else nothing — and `HttpError` then names the status
         * itself. This used to fall back to `response.statusText`, which is empty over HTTP/2, so a
         * refusal with no body (an authorization 403) threw an error whose message was "".
         */
        throw new HttpError(
            httpErrorBodyMessage(errorData),
            response.status,
            response.statusText,
            errorData
        );
    }

    /*
     * ★ A SUCCESS WITH NO BODY IS A SUCCESS (ROBO-583 Failed QA, 2026-09-28). `response.json()` on
     *   an empty body throws, and `Api.fetch` turns any throw into `ok:false` — so every `204 No
     *   Content` read as a failure. `POST checkout/reservations/cancel` released the hold and Hubby
     *   told the user it couldn't. Three API routes answer 204 today (all under Checkout). An
     *   empty body yields `data: undefined`; a body that is there but not JSON still throws, as
     *   before.
     */
    const text = await response.text();
    const result = (text === '' ? undefined : JSON.parse(text)) as T;

    return {
        data: result,
        headers: response.headers,
        ok: response.ok,
        redirected: response.redirected,
        status: response.status,
        statusText: response.statusText,
        url
    };
}

export function get<T>(path: string): Promise<ApiResponse<T>> {
    return request<T>('GET', path);
}

export function post<T, U>(path: string, data?: U): Promise<ApiResponse<T>> {
    return request<T, U>('POST', path, data);
}

export function put<T, U>(path: string, data?: U): Promise<ApiResponse<T>> {
    return request<T, U>('PUT', path, data);
}

export function patch<T, U>(path: string, data?: U): Promise<ApiResponse<T>> {
    return request<T, U>('PATCH', path, data);
}

export function del<T>(path: string): Promise<ApiResponse<T>> {
    return request<T>('DELETE', path);
}

/**
 * Fetches a binary document with the caller's bearer token, returning `null` on any refusal.
 *
 * ★★★ THIS EXISTS BECAUSE AN `<a href>` CANNOT CARRY A BEARER TOKEN (ROBO-253). Monthly Billing's
 * `View Invoice` links point at an authenticated API endpoint that streams a PDF; rendering them as
 * plain hyperlinks would send an unauthenticated request and get a 401, and the failure would look
 * like a broken invoice rather than a missing credential. The same reasoning is already recorded on
 * `expiring-domains.tsx`, whose CSV control is a button rather than a link for this exact reason.
 *
 * ★★ `request()` ABOVE ALWAYS CALLS `response.json()`, so it cannot be reused here — a PDF body
 * would throw on parse. This deliberately shares the URL and host resolution and nothing else.
 *
 * ★ A NON-OK RESPONSE YIELDS `null` RATHER THAN THROWING. The endpoint answers 404 for "not yours"
 * and "does not exist" alike, and the caller's job is to say "not available" either way — there is
 * no error detail worth surfacing and none worth logging on the client.
 */
export async function getBlob(path: string, bearerToken?: string): Promise<Blob | null> {
    const headers: HeadersInit = {};

    if (bearerToken) {
        headers.Authorization = `Bearer ${bearerToken}`;
    }

    const response = await fetch(path.includes('https://') ? path : apiUrlFetch(path), {
        method: 'GET',
        headers
    });

    if (!response.ok) {
        return null;
    }

    const blob = await response.blob();

    //★★ AN EMPTY BODY IS NOT A DOCUMENT. The API already collapses Intuit's empty-200 case to a 404,
    //so this is belt and braces — but a zero-byte blob opens as a blank tab, which reads as a broken
    //viewer rather than a missing invoice.
    return blob.size > 0 ? blob : null;
}
